import { useState, useRef, useEffect, useCallback } from 'react'
import type { Message, MessageFormData } from '@/types'
import { MessageStatus } from '@/types/enums'
import { NodeListItem, type MessageNodeConfig } from '@/components/nodes'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Reply, Send, Loader2, ChevronDown, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'
import { messageService } from '@/services'
import { useAuth } from '@/hooks/useAuth'

// Helper function to sort messages by creation date
const sortMessagesByDate = (messages: Message[]) => {
  return messages.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
}

// Component for displaying reply context
interface ReplyContextProps {
  replyingTo: Message
  currentMessage: Message
}

function ReplyContext({ replyingTo, currentMessage }: ReplyContextProps) {
  if (replyingTo.id === currentMessage.id) return null

  return (
    <div className="bg-muted/50 p-3 rounded-lg border-l-2 border-primary">
      <div className="flex items-center gap-2 mb-1">
        <Reply className="h-3 w-3 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">
          Replying to {replyingTo.creator.name}
        </span>
      </div>
      <p className="text-sm line-clamp-2">{replyingTo.description}</p>
    </div>
  )
}

// Component for the reply form
interface ReplyFormProps {
  replyContent: string
  setReplyContent: (content: string) => void
  loading: boolean
  replyingTo: Message | null
  currentMessage: Message
  textareaRef: React.RefObject<HTMLTextAreaElement>
  onReply: () => void
  onCancelReply: () => void
  onFocus: () => void
  hideCancel?: boolean
}

function ReplyForm({
  replyContent,
  setReplyContent,
  loading,
  replyingTo,
  currentMessage,
  textareaRef,
  onReply,
  onCancelReply,
  onFocus,
  hideCancel = false,
}: ReplyFormProps) {
  return (
    <>
      {/* Show context when replying to a specific message */}
      {replyingTo && <ReplyContext replyingTo={replyingTo} currentMessage={currentMessage} />}

      <div className="flex items-start gap-2">
        <div className="flex-1 space-y-3">
          <Textarea
            ref={textareaRef}
            placeholder={
              replyingTo
                ? replyingTo.id === currentMessage.id
                  ? 'Write your reply...'
                  : `Reply to ${replyingTo.creator.name}...`
                : 'Write your reply...'
            }
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            rows={3}
            disabled={loading}
            onFocus={onFocus}
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" onClick={onReply} disabled={loading || !replyContent.trim()}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Send Reply
                </>
              )}
            </Button>

            {!hideCancel && (
              <Button variant="outline" size="sm" onClick={onCancelReply} disabled={loading}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

// Component for rendering nested replies
interface NestedRepliesProps {
  nestedReplies: Message[]
  level: number
  showTitle: boolean
  showDescription: boolean
  showParentMessage: boolean
  showNestedReplies: boolean
  messageConfig: MessageNodeConfig
  onReplyCreated?: (updatedMessage: Message) => void
}

function NestedReplies({
  nestedReplies,
  level,
  showTitle,
  showDescription,
  showParentMessage,
  showNestedReplies,
  messageConfig,
  onReplyCreated,
}: NestedRepliesProps) {
  if (nestedReplies.length === 0) return null

  return (
    <>
      {sortMessagesByDate(nestedReplies).map((reply) => (
        <MessageThread
          key={reply.id}
          message={reply}
          level={level + 1}
          showTitle={showTitle}
          showDescription={showDescription}
          showParentMessage={showParentMessage}
          showNestedReplies={showNestedReplies}
          messageConfig={messageConfig}
          onReplyCreated={onReplyCreated}
        />
      ))}
    </>
  )
}

interface MessageThreadProps {
  /**
   * The message to display, including its nested replies
   */
  message: Message
  /**
   * The nesting level for visual indentation (0 for top-level messages)
   */
  level?: number
  /**
   * Additional CSS classes to apply to the container
   */
  className?: string
  /**
   * Whether to show the message title
   */
  showTitle?: boolean
  /**
   * Whether to show the message description/content
   */
  showDescription?: boolean
  /**
   * Whether to show the parent message (when level=0). If false, only shows replies
   */
  showParentMessage?: boolean
  /**
   * Whether to show nested replies by default. If false, shows a toggle button
   */
  showNestedReplies?: boolean
  /**
   * Configuration options for the NodeListItem component
   */
  messageConfig?: MessageNodeConfig
  /**
   * Callback when a reply is successfully created
   */
  onReplyCreated?: (updatedMessage: Message) => void
}

export function MessageThread({
  message,
  level = 0,
  className = '',
  showTitle = true,
  showDescription = true,
  showParentMessage = true,
  showNestedReplies = true,
  messageConfig = {},
  onReplyCreated,
}: MessageThreadProps) {
  const { user } = useAuth()
  const [replyContent, setReplyContent] = useState('')
  const [loading, setLoading] = useState(false)
  const [showReplyForm, setShowReplyForm] = useState(false)
  const [replyingTo, setReplyingTo] = useState<Message | null>(null)

  // Use messageConfig.showNestedReplies if provided, otherwise fall back to showNestedReplies prop
  const effectiveShowNestedReplies = messageConfig.showNestedReplies ?? showNestedReplies
  const [isNestedRepliesVisible, setIsNestedRepliesVisible] = useState(effectiveShowNestedReplies)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Filter out draft messages from nested replies
  const nestedReplies =
    message.messages?.filter((reply) => reply.status !== MessageStatus.Draft) || []

  const handleStartReply = (targetMessage: Message | null = null) => {
    setReplyingTo(targetMessage || message)
    setShowReplyForm(true)
  }

  const toggleNestedReplies = () => {
    setIsNestedRepliesVisible(!isNestedRepliesVisible)
  }

  const resetReplyForm = useCallback(() => {
    setReplyContent('')
    setReplyingTo(null)
    // Reset textarea ref
    if (textareaRef.current) {
      textareaRef.current.value = ''
    }
  }, [])

  const handleCancelReply = useCallback(() => {
    setShowReplyForm(false)
    resetReplyForm()
  }, [resetReplyForm])

  // Focus textarea when reply form is shown
  useEffect(() => {
    if (showReplyForm && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [showReplyForm])

  // Handle escape key to close reply form
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showReplyForm) {
        handleCancelReply()
      }
    }

    if (showReplyForm) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [showReplyForm, handleCancelReply])

  // Sync nested replies visibility with prop changes
  useEffect(() => {
    setIsNestedRepliesVisible(effectiveShowNestedReplies)
  }, [effectiveShowNestedReplies])

  const handleReply = async () => {
    if (!user || !replyContent.trim() || !replyingTo) return

    setLoading(true)
    try {
      const replyData: MessageFormData = {
        name: `Re: ${replyingTo.name}`,
        description: replyContent,
        directMessage: replyingTo.directMessage,
        workspaceId: replyingTo.workspace?.id,
        userIds: replyingTo.users.map((u) => u.id),
        // Inherit parent's associations
        taskIds: replyingTo.tasks.map((t) => t.id),
        projectIds: replyingTo.projects.map((p) => p.id),
        documentIds: replyingTo.documents.map((d) => d.id),
      }

      const { updatedParent } = await messageService.replyToMessage(
        replyingTo.id,
        replyData,
        user.id
      )

      // Update the UI with the new reply
      if (replyingTo.id === message.id) {
        // If replying to the root message, update the entire message tree
        onReplyCreated?.(updatedParent)
      } else {
        // If replying to a nested message, we need to update the parent message
        // This is a simplified approach - in a real app, you'd want to update the specific nested message
        onReplyCreated?.(message)
      }

      setShowReplyForm(false)
      resetReplyForm()
      toast.success('Reply sent successfully')
    } catch (error) {
      console.error('Failed to send reply:', error)
      toast.error('Failed to send reply')
    } finally {
      setLoading(false)
    }
  }

  // If we're at the top level and should hide the parent message, only show replies
  if (level === 0 && !showParentMessage) {
    return (
      <div className={className}>
        {/* Render nested replies recursively */}
        <NestedReplies
          nestedReplies={nestedReplies}
          level={level}
          showTitle={showTitle}
          showDescription={showDescription}
          showParentMessage={showParentMessage}
          showNestedReplies={isNestedRepliesVisible}
          messageConfig={messageConfig}
          onReplyCreated={onReplyCreated}
        />

        {/* Show reply form only at root level or when explicitly triggered */}
        {(level === 0 && messageConfig.showRootReplyForm) || showReplyForm ? (
          <ReplyForm
            replyContent={replyContent}
            setReplyContent={setReplyContent}
            loading={loading}
            replyingTo={replyingTo}
            currentMessage={message}
            textareaRef={textareaRef}
            onReply={handleReply}
            onCancelReply={handleCancelReply}
            onFocus={() => {
              // If no reply is in progress, start replying to the current message
              if (!showReplyForm) {
                handleStartReply(message)
              }
            }}
            hideCancel={level === 0}
          />
        ) : null}
      </div>
    )
  }

  return (
    <div className={`${level > 0 ? 'ml-3 pl-3 border-l-2 border-muted pb-3' : ''} ${className}`}>
      <div className={level > 0 ? 'relative' : ''}>
        {/* Render the current message */}
        <div className="group relative">
          <NodeListItem
            item={message}
            context="message"
            config={{
              // Default configuration
              showReplyCount: true,
              showMessageType: true,
              showReadStatus: true,
              // Merge with provided config, but prioritize showTitle/showDescription props for backward compatibility
              ...messageConfig,
              showDescription: messageConfig.showDescription ?? showDescription,
              showTitle: messageConfig.showTitle ?? showTitle,
            }}
          />

          {/* Reply button */}
          <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleStartReply(message)}
              className="h-7 px-2"
              title="Reply to this message"
            >
              <Reply className="h-3 w-3 mr-1" />
              <span className="text-xs">Reply</span>
            </Button>
          </div>
        </div>

        {/* Visual connector for nested threads */}
        {level > 0 && (
          <div className="absolute -left-[.875rem] top-4 h-2 w-[.875rem] border-b-2 border-l-2 border-muted rounded-bl-md"></div>
        )}
      </div>

      {/* Render nested replies recursively */}
      {nestedReplies.length > 0 && (
        <div className="mt-3">
          {/* Show replies toggle button when replies are hidden */}
          {!isNestedRepliesVisible && (
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleNestedReplies}
              className="mb-2 text-muted-foreground hover:text-foreground"
            >
              <ChevronRight className="h-4 w-4 mr-1" />
              Show {nestedReplies.length} {nestedReplies.length === 1 ? 'reply' : 'replies'}
            </Button>
          )}

          {/* Show nested replies when visible */}
          {isNestedRepliesVisible && (
            <>
              {/* Hide replies button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleNestedReplies}
                className="mb-2 text-muted-foreground hover:text-foreground"
              >
                <ChevronDown className="h-4 w-4 mr-1" />
                Hide {nestedReplies.length} {nestedReplies.length === 1 ? 'reply' : 'replies'}
              </Button>

              <NestedReplies
                nestedReplies={nestedReplies}
                level={level}
                showTitle={showTitle}
                showDescription={showDescription}
                showParentMessage={showParentMessage}
                showNestedReplies={isNestedRepliesVisible}
                messageConfig={messageConfig}
                onReplyCreated={onReplyCreated}
              />
            </>
          )}
        </div>
      )}

      {/* Show reply form only at root level or when explicitly triggered */}
      {(level === 0 && messageConfig.showRootReplyForm) || showReplyForm ? (
        <div className="mt-4 pb-4">
          <ReplyForm
            replyContent={replyContent}
            setReplyContent={setReplyContent}
            loading={loading}
            replyingTo={replyingTo}
            currentMessage={message}
            textareaRef={textareaRef}
            onReply={handleReply}
            onCancelReply={handleCancelReply}
            onFocus={() => {
              // If no reply is in progress, start replying to the current message
              if (!showReplyForm) {
                handleStartReply(message)
              }
            }}
            hideCancel={level === 0}
          />
        </div>
      ) : null}
    </div>
  )
}
